import { isKnownStopKey, type VoyageStop } from '@lb/domain'

import { routePathForContent } from '../content-routes'

/**
 * The voyage map's rules that are not drawing: the order stops come in, which
 * ones share a tap target, what each one is called, and where « Open » goes.
 * Kept out of the SVG component so each rule is a function a test can call.
 */

/**
 * Chronological, whatever order the wire used.
 *
 * The server sorts by day with a fixed tiebreak, and this keeps that tiebreak
 * — `Array.prototype.sort` is stable — while making "left to right is earlier
 * to later" true of the picture even if a future server stops promising it.
 */
export function inDayOrder<T extends Pick<VoyageStop, 'day'>>(stops: readonly T[]): T[] {
  return [...stops].sort((a, b) => a.day - b.day)
}

/**
 * The tap target's half-width, in viewBox units. The drawn dot is 9–12; a
 * target that size is a few real pixels on a phone, so the target is wider.
 */
export const HIT_R = 24

/**
 * Stops close enough to share one tap target.
 *
 * Every stop used to carry its own transparent 22-unit circle, drawn in day
 * order — so a later stop's circle covered an earlier one's wherever they
 * overlapped, and stops on the same day sat exactly on top of each other:
 * on a member's day 1, « Welcome aboard » and « First post » are one dot and
 * only the second could ever be tapped. Parents reported the map as having
 * « nothing tappable ».
 *
 * So stops are grouped left to right: a group takes every stop within `gap`
 * of its FIRST stop, and the next stop beyond that starts a new group. Each
 * group is ONE control, and the card it opens lists every stop in it.
 * Measuring from the first stop rather than the previous one keeps a group
 * from chaining across a busy year — a member posting weekly would otherwise
 * be one target from January to December.
 */
export function clusterStops<T extends { x: number }>(placed: readonly T[], gap = HIT_R * 2): T[][] {
  const clusters: T[][] = []
  for (const stop of placed) {
    const current = clusters[clusters.length - 1]
    const first = current?.[0]
    if (current && first && stop.x - first.x < gap) current.push(stop)
    else clusters.push([stop])
  }
  return clusters
}

/**
 * Each group's tap area along the path: `HIT_R` beyond its outermost stops,
 * but never past the midpoint to a neighbouring group — so no target covers
 * another, and every one can be reached. The path runs left to right, so
 * splitting on x is enough.
 */
export function targetSpans(clusters: ReadonlyArray<ReadonlyArray<{ x: number }>>): Array<{ x0: number; x1: number }> {
  const ends = clusters.map((c) => {
    const xs = c.map((s) => s.x)
    return { min: Math.min(...xs), max: Math.max(...xs) }
  })
  return ends.map((end, i) => {
    const prev = ends[i - 1]
    const next = ends[i + 1]
    const x0 = prev ? Math.max(end.min - HIT_R, (prev.max + end.min) / 2) : end.min - HIT_R
    const x1 = next ? Math.min(end.max + HIT_R, (end.max + next.min) / 2) : end.max + HIT_R
    return { x0, x1 }
  })
}

/**
 * A stop's name.
 *
 * `title` when the API has one, and the `key` when it does not. That fallback
 * is why we mirror `key` at all: a course stop whose course was later
 * unpublished comes back with `title: null`, and it still happened. "Course
 * started" is true; a blank line is not. A key this build has no copy for says
 * « Stop » — true, if vague — rather than printing its i18n path.
 */
export function labelFor(stop: Pick<VoyageStop, 'key' | 'title'>, t: (key: string) => string): string {
  if (stop.title) return stop.title
  return isKnownStopKey(stop.key) ? t(`voyage.key.${stop.key}`) : t('voyage.key.unknown')
}

/** Where a stop's « Open » goes, or null when there is nothing to open. */
export type StopDestination =
  | { kind: 'course'; id: string }
  | { kind: 'thread'; roomId: string; postId: string }
  /** A piece of content, as an app path for `<Link to>` — from `contentPath` + `refId`. */
  | { kind: 'content'; to: string }
  | { kind: 'challenge' }

/**
 * The page a stop is about.
 *
 * - A course stop (started, completed, a module of it) opens the course — for
 *   `moduleCompleted` the `refId` is the COURSE's id and the title is the
 *   module's, so the course page is still the right door.
 * - A post stop (`firstPost`, `post`) opens its thread, which needs the room:
 *   the only thread route is `/community/$roomId/posts/$postId` and there is no
 *   by-post-id read that would find the room. The server sends `roomId` on
 *   these since 25 Sep 2026; one without it (an older server) gets no « Open »
 *   rather than a link that lands on « not found ».
 * - An anchor (`firstAnchor`, `anchor`) is either: a post kept with ⚓ carries
 *   `roomId` and opens its thread, and a piece of content kept with ⚓ carries
 *   `contentPath` and opens that content's page (`routePathForContent`, which
 *   answers null for a type with no page of its own).
 * - A challenge period opens the challenge screen, which has no per-period
 *   route: it shows the current period and the member's history.
 * - Milestones and `storm` name nothing that can be opened.
 */
export function stopDestination(
  stop: Pick<VoyageStop, 'key' | 'refId' | 'roomId' | 'contentPath'>,
): StopDestination | null {
  switch (stop.key) {
    case 'courseStarted':
    case 'courseCompleted':
    case 'moduleCompleted':
      return stop.refId ? { kind: 'course', id: stop.refId } : null
    case 'firstPost':
    case 'post':
    case 'firstAnchor':
    case 'anchor': {
      if (!stop.refId) return null
      if (stop.roomId) return { kind: 'thread', roomId: stop.roomId, postId: stop.refId }
      const to = stop.contentPath ? routePathForContent(stop.contentPath, stop.refId) : null
      return to ? { kind: 'content', to } : null
    }
    case 'challengePeriod':
      return { kind: 'challenge' }
    default:
      return null
  }
}
